import { createFeature, createReducer, on } from '@ngrx/store';
import { SupplierActions } from './supplier.actions';
import { SupplierState } from './supplier-state.model';

const initialState: SupplierState = {
  suppliers: [],
  isLoading: false,
  error: null,
  blends: [],
  blendsLoading: false,
  blendsError: null,
  blendFormMode: 'closed',
  selectedBlendId: null,
  blendSaving: false,
  blendSaveError: null,
  groupTemplates: [],
  groupTemplatesLoading: false,
  groupTemplatesError: null,
  groupTemplateSaving: false,
  groupTemplateSaveError: null,
};

export const supplierFeatureKey = 'supplier';

export const supplierReducer = createReducer(
  initialState,

  on(SupplierActions.loadSuppliers, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),

  on(SupplierActions.loadSuppliersSuccess, (state, { suppliers }) => ({
    ...state,
    suppliers,
    isLoading: false,
    error: null,
  })),

  on(SupplierActions.loadSuppliersFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(SupplierActions.updateSupplier, (state, { id, data }) => ({
    ...state,
    suppliers: state.suppliers.map((supplier) =>
      supplier.id === id ? { ...supplier, ...data } : supplier,
    ),
  })),

  on(SupplierActions.saveSupplierAccess, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),

  on(SupplierActions.saveSupplierAccessSuccess, (state) => ({
    ...state,
    isLoading: false,
    error: null,
  })),

  on(SupplierActions.saveSupplierAccessFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(SupplierActions.loadSupplierBlends, (state) => ({
    ...state,
    blendsLoading: true,
    blendsError: null,
  })),

  on(SupplierActions.loadSupplierBlendsSuccess, (state, { blends }) => ({
    ...state,
    blends,
    blendsLoading: false,
    blendsError: null,
  })),

  on(SupplierActions.loadSupplierBlendsFailure, (state, { error }) => ({
    ...state,
    blendsLoading: false,
    blendsError: error,
  })),

  on(SupplierActions.deleteSupplierBlend, (state) => ({
    ...state,
    blendsLoading: true,
    blendsError: null,
  })),

  on(SupplierActions.deleteSupplierBlendSuccess, (state, { id }) => ({
    ...state,
    blends: state.blends.filter((blend) => blend.id !== id),
    blendsLoading: false,
    blendsError: null,
  })),

  on(SupplierActions.deleteSupplierBlendFailure, (state, { error }) => ({
    ...state,
    blendsLoading: false,
    blendsError: error,
  })),

  on(SupplierActions.setSupplierBlendDefault, (state, { id, isDefault }) => ({
    ...state,
    blends: state.blends.map((blend) => ({
      ...blend,
      isDefault: blend.id === id ? isDefault : false,
    })),
    blendsLoading: true,
    blendsError: null,
  })),

  on(SupplierActions.setSupplierBlendDefaultSuccess, (state) => ({
    ...state,
    blendsLoading: false,
    blendsError: null,
  })),

  on(SupplierActions.setSupplierBlendDefaultFailure, (state, { error }) => ({
    ...state,
    blendsLoading: false,
    blendsError: error,
  })),

  on(SupplierActions.lockAllSupplierBlends, (state) => ({
    ...state,
    blendsLoading: true,
    blendsError: null,
  })),

  on(SupplierActions.lockAllSupplierBlendsSuccess, (state, { isLocked }) => ({
    ...state,
    blends: state.blends.map((blend) => ({ ...blend, isLocked })),
    blendsLoading: false,
    blendsError: null,
  })),

  on(SupplierActions.lockAllSupplierBlendsFailure, (state, { error }) => ({
    ...state,
    blendsLoading: false,
    blendsError: error,
  })),

  on(SupplierActions.openCreateSupplierBlendForm, (state) => ({
    ...state,
    blendFormMode: 'create' as const,
    selectedBlendId: null,
    blendSaveError: null,
  })),

  on(SupplierActions.openEditSupplierBlendForm, (state, { id }) => ({
    ...state,
    blendFormMode: 'edit' as const,
    selectedBlendId: id,
    blendSaveError: null,
  })),

  on(SupplierActions.closeSupplierBlendForm, (state) => ({
    ...state,
    blendFormMode: 'closed' as const,
    selectedBlendId: null,
    blendSaveError: null,
  })),

  on(SupplierActions.createSupplierBlend, (state) => ({
    ...state,
    blendSaving: true,
    blendSaveError: null,
  })),

  on(SupplierActions.createSupplierBlendSuccess, (state, { blend }) => ({
    ...state,
    blends: [...state.blends, blend],
    blendSaving: false,
    blendSaveError: null,
    blendFormMode: 'closed' as const,
    selectedBlendId: null,
  })),

  on(SupplierActions.createSupplierBlendFailure, (state, { error }) => ({
    ...state,
    blendSaving: false,
    blendSaveError: error,
  })),

  on(SupplierActions.updateSupplierBlend, (state) => ({
    ...state,
    blendSaving: true,
    blendSaveError: null,
  })),

  on(
    SupplierActions.updateSupplierBlendSuccess,
    (state, { blend, keepFormOpen }) => ({
      ...state,
      blends: state.blends.map((existing) =>
        existing.id === blend.id ? blend : existing,
      ),
      blendSaving: false,
      blendSaveError: null,
      blendFormMode: keepFormOpen ? state.blendFormMode : ('closed' as const),
      selectedBlendId: keepFormOpen ? state.selectedBlendId : null,
    }),
  ),

  on(SupplierActions.updateSupplierBlendFailure, (state, { error }) => ({
    ...state,
    blendSaving: false,
    blendSaveError: error,
  })),

  on(SupplierActions.loadGroupTemplates, (state) => ({
    ...state,
    groupTemplatesLoading: true,
    groupTemplatesError: null,
  })),

  on(SupplierActions.loadGroupTemplatesSuccess, (state, { templates }) => ({
    ...state,
    groupTemplates: templates,
    groupTemplatesLoading: false,
    groupTemplatesError: null,
  })),

  on(SupplierActions.loadGroupTemplatesFailure, (state, { error }) => ({
    ...state,
    groupTemplatesLoading: false,
    groupTemplatesError: error,
  })),

  on(SupplierActions.saveGroupAsTemplate, (state) => ({
    ...state,
    groupTemplateSaving: true,
    groupTemplateSaveError: null,
  })),

  on(SupplierActions.saveGroupAsTemplateSuccess, (state) => ({
    ...state,
    groupTemplateSaving: false,
    groupTemplateSaveError: null,
  })),

  on(SupplierActions.saveGroupAsTemplateFailure, (state, { error }) => ({
    ...state,
    groupTemplateSaving: false,
    groupTemplateSaveError: error,
  })),
);

export const supplierFeature = createFeature({
  name: supplierFeatureKey,
  reducer: supplierReducer,
});
